import { Component, OnDestroy, OnInit } from '@angular/core';
import { NavigationService } from '../../../../service/services/misc/navigation/navigation.service';
import { ActivatedRoute } from '@angular/router';
import { firstValueFrom, Subscription } from 'rxjs';
import { SwaService } from '../../../../service/services/misc/swa/swa.service';
import {OidcLoginDialogComponent} from "../../dialogs/oidc-login-dialog/oidc-login-dialog.component";
import {DialogService} from "../../../../service/services/misc/dialog/dialog.service";
import {LastLoginConfigService} from "../../../../service/services/misc/last-login-config/last-login-config.service";
import {HttpService} from "../../../../service/services/rest/http-service/http.service";
import {Utils} from "../../../../core/helpers/Utils";

interface LoginParams {
    idp?: string;
    identifier?: string;
    autoLogin: boolean;
}

@Component({
    selector: 'app-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    standalone: false
})
export class LoginComponent implements OnInit, OnDestroy {
    redirectUrl: string | null;
    subscriptions: Subscription[] = [];
    error: string;
    selectedProvider: string;
    isAutoLogging = false;
    infoTextMap: { [key: string]: string } = {
        'sap.ids': 'Log in with your SAP ID using SAP Single Sign-On.',
        'sap.opscurity': 'Log in with opscurity IdP to access productive landscapes. This login option requires access to a dedicated CAM profile.',
        'sap.opscurity-dev':'Log in with opscurity-dev IdP to access development landscapes. This login option requires access to a dedicated CAM profile.',
        'custom.idp': 'Log in using a custom OpenID Connect identity provider registered in Unified Resource Manager.'
    };

    // Valid IDP values
    private readonly VALID_IDPS = ['sap.ids', 'sap.opscurity', 'sap.opscurity-dev', 'custom.idp'];
    // Pattern for OIDC identifier validation
    private readonly IDENTIFIER_PATTERN = /^[a-zA-Z0-9._-]{1,128}$/;
    private readonly PATH_PATTERN = /^\/[-\w.]+(\/[-\w.]+)*$/;

    constructor(
		private _navigationService: NavigationService,
		private route: ActivatedRoute,
		private swaService: SwaService,
        protected dialogService: DialogService,
        private lastLoginConfigService: LastLoginConfigService,
        private httpService: HttpService<any>
    ) {
        this.selectedProvider =  'sap.ids';
    }

    get isPrivateCloud(): boolean {
        return Utils.isPrivateCloud;
    }

    get hideOpscurity(): boolean {
        return Utils.hideOpscurity;
    }

    login(idp: string, oidcPath?: string): void {
        this.swaService.sendEvent(this.swaService.SWA_EVENT_TYPES.LOGIN_CLICKED);

        // Save to storage before redirecting
        this.lastLoginConfigService.save(idp, oidcPath);

        // redirect with idp and optionally oidcPath
        this._navigationService.navigateToLogin(
            this.redirectUrl || window.location.pathname,
            idp,
            oidcPath
        );
    }

    loginByIdp(idp: string): void {
        if (idp == 'custom.idp') {
            this.openOIDCLoginDialog();
        } else {
            this.login(idp);
        }
    }

    openOIDCLoginDialog(): void {
        const dialogRef = this.dialogService.open(OidcLoginDialogComponent, {width:"475px", minHeight:"250px"});
        dialogRef.data = { redirectUrl: this.redirectUrl };
        dialogRef.afterClosed.subscribe(customPath => {
            if (customPath) {
                this.login('custom.idp', customPath);
            }
        });
    }


    ngOnInit(): void {
        this.swaService.sendEvent(this.swaService.SWA_EVENT_TYPES.LOGIN_PAGE_LOADED);
        this.subscriptions.push(
            this.route.queryParams.subscribe((params) => {
                this.redirectUrl = params['redirectUrl'];
                let error = params['error'];
                if (!this.error && error) {
                    this.error = error;
                    this.clearError();
                }

                // Parse and handle login parameters
                const loginParams = this.parseLoginParams(params);
                this.initializeLoginState(loginParams);
            })
        );
    }

    /**
     * Parse login parameters from URL query params
     */
    private parseLoginParams(params: any): LoginParams {
        return {
            idp: this.validateIdp(params['idp']),
            identifier: params['identifier'],
            autoLogin: params['autoLogin'] === 'true'
        };
    }

    /**
     * Validate IDP parameter
     */
    private validateIdp(idp: string | undefined): string | undefined {
        if (!idp) return undefined;
        if (!this.VALID_IDPS.includes(idp)) return undefined;

        // On private cloud, reject opscurity IDPs
        if (this.isPrivateCloud && (idp === 'sap.opscurity' || idp === 'sap.opscurity-dev')) {
            return undefined;
        }

        return idp;
    }

    /**
     * Initialize login state based on URL parameters or stored config
     */
    private initializeLoginState(urlParams: LoginParams): void {
        if (urlParams.idp) {
            // URL params take precedence
            this.selectedProvider = urlParams.idp;

            if (urlParams.idp === 'custom.idp' && urlParams.identifier) {
                this.handleCustomIdpWithIdentifier(urlParams.identifier, urlParams.autoLogin);
            } else if (urlParams.idp === 'custom.idp' && urlParams.autoLogin) {
                // custom.idp with autoLogin but no identifier - show error
                this.error = 'Missing OIDC identifier for custom provider. Please provide an identifier.';
                this.selectedProvider = 'custom.idp';
            } else if (urlParams.autoLogin) {
                this.handleAutoLogin(urlParams.idp);
            }
        } else {
            // No URL params - check storage
            this.loadLastSuccessfulLogin();
        }
    }

    /**
     * Load last successful login config from storage and pre-select dropdown
     */
    private loadLastSuccessfulLogin(): void {
        const config = this.lastLoginConfigService.load();
        if (config) {
            // If private cloud and stored IDP is opscurity, reset to default
            if (this.isPrivateCloud && (config.idp === 'sap.opscurity' || config.idp === 'sap.opscurity-dev')) {
                this.selectedProvider = 'sap.ids';
            } else {
                this.selectedProvider = config.idp;
            }
            // Do NOT auto-open dialog or auto-login from storage
        }
    }

    /**
     * Handle auto-login for non-custom IDPs
     */
    private handleAutoLogin(idp: string): void {
        if (this.error) {
            // Don't auto-login if there's already an error
            return;
        }

        this.isAutoLogging = true;
        // Trigger login immediately
        this.login(idp);
    }

    /**
     * Handle custom IDP with identifier from URL
     */
    private async handleCustomIdpWithIdentifier(identifier: string, autoLogin: boolean): Promise<void> {
        if (!this.validateIdentifier(identifier)) {
            this.error = 'Invalid OIDC identifier format. Please check the identifier and try again.';
            this.openOIDCLoginDialogWithIdentifier(identifier);
            return;
        }

        if (autoLogin) {
            if (this.error) {
                // Don't auto-login if there's already an error
                this.openOIDCLoginDialogWithIdentifier(identifier);
                return;
            }

            this.isAutoLogging = true;
            const isValid = await this.validateOidcProvider(identifier);

            if (isValid) {
                this.login('custom.idp', identifier);
            } else {
                this.isAutoLogging = false;
                this.error = 'Cannot find the OIDC provider resource. Please verify the identifier or path.';
                this.openOIDCLoginDialogWithIdentifier(identifier);
            }
        } else {
            // Just open dialog with pre-filled identifier
            this.openOIDCLoginDialogWithIdentifier(identifier);
        }
    }

    /**
     * Validate OIDC identifier format
     */
    private validateIdentifier(identifier: string): boolean {
        return this.IDENTIFIER_PATTERN.test(identifier) || this.PATH_PATTERN.test(identifier);
    }

    /**
     * Validate OIDC provider exists via backend
     */
    private async validateOidcProvider(identifier: string): Promise<boolean> {
        try {
            await firstValueFrom(
                this.httpService.get(`/login/validate?path=${encodeURIComponent(identifier)}`)
            );
            return true;
        } catch (err) {
            console.error('OIDC validation failed:', err);
            return false;
        }
    }

    /**
     * Open OIDC dialog with pre-filled identifier
     */
    private openOIDCLoginDialogWithIdentifier(identifier: string): void {
        const dialogRef = this.dialogService.open(OidcLoginDialogComponent, {
            width: "475px",
            minHeight: "250px"
        });
        dialogRef.data = {
            redirectUrl: this.redirectUrl,
            identifier: identifier
        };
        dialogRef.afterClosed.subscribe(customPath => {
            if (customPath) {
                this.login('custom.idp', customPath);
            }
        });
    }

    public clearError() {
        return this._navigationService.navigateToLoginPage({
            queryParamsHandling: 'merge',
            queryParams: { error: null },
            replaceUrl: true,
        });
    }

    ngOnDestroy(): void {
        this.subscriptions.forEach((s) => s.unsubscribe());
    }
}
